'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Loader2 } from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { toast } from '@/shared/lib/toast';
import { supportService } from '../api/support.service';
import { MAX_MESSAGE_LENGTH, MIN_MESSAGE_LENGTH } from '../types';

/**
 * The one-field quick feedback form, shared by every surface that offers a
 * drive-by remark: the wide-screen header popover (`FeedbackHeaderButton`) and
 * the phone ⋮ menu dialog (`FeedbackQuickDialog`). Same support-ticket
 * mechanism as Account → Feedback, with the category pinned to
 * `general_inquiry` and no attachments.
 */
export function QuickFeedbackComposer({ onSent }: { onSent: () => void }) {
  const t = useTranslations('feedback');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canSubmit =
    message.trim().length >= MIN_MESSAGE_LENGTH && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      await supportService.submitFeedback({
        category: 'general_inquiry',
        message: message.trim(),
      });
      // Confirmation is the app's standard toast — the same one the full
      // Account → Feedback form shows. The surface closes right away rather
      // than holding a second, local success panel: two confirmations read as
      // "sent twice". Apple and Android do the same.
      setMessage('');
      onSent();
      toast.success(t('form.success.toast'));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t('form.errors.submitFailed')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-3">
      <Textarea
        autoFocus
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={t('quick.placeholder')}
        maxLength={MAX_MESSAGE_LENGTH}
        disabled={isSubmitting}
        rows={5}
        className="resize-none min-h-[120px] text-composer-input"
      />
      {/* The note takes the FULL width on its own line, with the button below
          it. Sharing one line with the button left the note barely half of a
          360px panel, where it needs three lines in the longer languages and
          got truncated mid-sentence. Same arrangement on Apple/Android. */}
      <p className="text-form-help text-muted-foreground">
        {t('form.reassurance')}
      </p>
      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('form.actions.submitting')}
            </>
          ) : (
            t('form.actions.submit')
          )}
        </Button>
      </div>
    </div>
  );
}
